/**
 * Job Side Effects Service
 *
 * Unified pipeline for all fire-and-forget side effects that follow a job state
 * transition. Replaces the drift-prone split between:
 *   - Inline blocks in transitionJobState + completeWithReturnVisit (online path)
 *   - Hand-coded re-implementations in offline-queue.runPostSyncEffects (replay path)
 *
 * Both online transitions and offline replay now route through executeJobSideEffects,
 * eliminating the SP8 drift class documented in AUDIT_NOTES.md.
 *
 * Side effects covered (inventory from AUDIT_NOTES audit pass):
 *   #2  Deal active-state sync (activeJobState/activeJobType)
 *   #3a Pre-site completion data → deal (presiteCompletionNote etc.)
 *   #3b Pre-site: advance deal PRESITE → QUOTING
 *   #4  Fault: sync completion back to fault (syncJobCompletionToFault)
 *   #5  Install: sync completion to deal + advance to REVIEW
 *   #6  Return visit: resolve tech name (internal helper)
 *   #7  Return visit: create follow-up job (+ AI briefing)
 *   #8  Return visit: sync return-visit fields + activityLog to deal
 *   #9  AI summary backfill (offline-only, gated by meta.needsAISummaryBackfill)
 *   #10 Install: promote services to Active + sync Starlink kit metadata
 *       (wires up previously-dead activateServicesOnJobCompletion — fixes the
 *        latent bug where Starlink kit metadata never synced to customer_services)
 *
 * Ordering invariant: the caller (online or replay) must have already applied
 * the primary updateDoc(jobs/{id}) before invoking executeJobSideEffects.
 * This helper re-fetches the job from Firestore so every effect sees fresh data.
 */

import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where,
  arrayUnion,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { stripUndefinedDeep } from '@/utils/firestore';
import { DEFAULT_TENANT_ID, getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import type { Job, JobState } from '@/types/schema';
import type { OfflineAction } from './offline-queue.service';
import { advanceDealStage } from './deal-workflow.service';
import { DealStage } from '@/types/theo-workflow.types';
import { syncJobCompletionToFault } from './fault.service';
import {
  activateServicePlansForCustomer,
  activateServicesOnJobCompletion,
} from './customer-service-activation.service';
import { propagateJobCompletionToCableRuns } from './project-cable.service';
import { hasFeatureSync } from '@/utils/featurePacks';
import { logUsageEvent } from './usage-telemetry.service';
import type { Project } from '@/types/schema';

function getTenantId(): string {
  try {
    return getTenantIdSync();
  } catch {
    // localStorage can throw in locked-down browser modes.
    return DEFAULT_TENANT_ID;
  }
}

async function resolveTechName(techId: string): Promise<string> {
  try {
    const snap = await getDoc(doc(db, 'users', techId));
    if (snap.exists()) {
      return (snap.data() as { name?: string }).name || techId;
    }
  } catch {
    /* fallback to techId */
  }
  return techId;
}

// ============================================================================
// PUBLIC ENTRY POINT
// ============================================================================

/**
 * Outcome summary returned by executeJobSideEffects. Callers that care about
 * post-sync visibility (the offline replay loop) can inspect `errors` to
 * surface failures up to a queue UI; online callers continue to ignore the
 * return value (errors are also persisted to the job's lastSideEffectError).
 */
export interface SideEffectResult {
  errors: Array<{ source: string; message: string }>;
}

/**
 * Execute all side effects for a job state transition.
 * Called from:
 *   - transitionJobState (online, after updateDoc commits)
 *   - completeWithReturnVisit (online, after updateDoc commits)
 *   - offline-queue.runPostSyncEffects (after replay updateDoc commits)
 *
 * Always re-fetches the job from Firestore so every effect sees the primary
 * write. Never throws — all failures are logged and swallowed to match the
 * fire-and-forget contract of the original call sites. The returned
 * SideEffectResult lets the offline replay path differentiate "ran clean" from
 * "ran but had N internal failures" so the queue can surface the difference.
 */
export async function executeJobSideEffects(
  action: OfflineAction,
): Promise<SideEffectResult> {
  if (action.type !== 'job_state_transition' && action.type !== 'return_visit') {
    return { errors: [] };
  }
  if (!action.docId) return { errors: [] };

  // Collect errors from each effect so we can persist them to the job doc at the
  // end. Each catch still logs to console for dev visibility; this array is the
  // durable, observable record (see `lastSideEffectError` write below).
  // Smallest version of the S1 workflowErrors pattern — future S1 work can
  // promote this to a per-entity subcollection.
  const sideEffectErrors: Array<{ source: string; message: string }> = [];

  // Re-fetch the job once so every effect operates on fresh Firestore state.
  let job: Job;
  try {
    const snap = await getDoc(doc(db, 'jobs', action.docId));
    if (!snap.exists()) {
      const msg = `Job ${action.docId} not found — skipping side effects`;
      console.warn(`[JobSideEffects] ${msg}`);
      return { errors: [{ source: 'job-not-found', message: msg }] };
    }
    job = { id: snap.id, ...snap.data() } as Job;
  } catch (err) {
    const msg = errMessage(err);
    console.error('[JobSideEffects] Failed to re-fetch job:', err);
    // Can't record on the job doc if we can't read it; surface via return only.
    return { errors: [{ source: 'job-refetch-failed', message: msg }] };
  }

  const meta = action.meta || {};
  const jobType: string | undefined = (meta.jobType as string | undefined) || job.type;
  const dealId: string | null = (meta.dealId as string | null | undefined) ?? job.dealId ?? null;
  const faultId: string | null =
    (meta.faultId as string | null | undefined) ?? (job.faultId ?? null);
  const techId = action.techId;
  const toState = action.data?.jobState as JobState | undefined;
  const now = new Date().toISOString();

  // Usage counter for the platform console — a completion is the single
  // strongest "this tenant is really using Theo" signal. Counter only.
  if (toState === 'COMPLETED') logUsageEvent('job_completed');

  // ── #2 Deal active-state sync — fires on EVERY transition with a linked deal
  //    (including non-terminal: EN_ROUTE → WORKING etc.)
  if (dealId) {
    const dealUpdate: Record<string, unknown> = {
      activeJobState: toState ?? null,
      activeJobType: jobType || null,
      updatedAt: now,
    };
    if (toState === 'COMPLETED' || toState === 'NEEDS_RETURN_VISIT') {
      dealUpdate.activeJobState = null;
      dealUpdate.activeJobType = null;
    }
    try {
      await updateDoc(doc(db, 'deals', dealId), dealUpdate);
    } catch (err) {
      console.error('[JobSideEffects] Deal active-state sync failed:', err);
      sideEffectErrors.push({ source: 'deal-active-state-sync', message: errMessage(err) });
    }
  }

  // Remaining effects are terminal-only. Bail for intermediate transitions.
  if (toState !== 'COMPLETED' && toState !== 'NEEDS_RETURN_VISIT') {
    await recordSideEffectErrors(action.docId, sideEffectErrors);
    return { errors: sideEffectErrors };
  }

  // ── #9 AI summary backfill (offline-only) ──
  // Only set when the queued action was enqueued with needsAISummaryBackfill=true.
  // Online path saves summary via saveSummaryToJob BEFORE transitioning, so the
  // re-fetch above already includes it.
  // Covers both COMPLETED and NEEDS_RETURN_VISIT — both are terminal states
  // where the job is done from the tech's perspective and deserves a summary.
  // (Before M14 fix, NEEDS_RETURN_VISIT was silently excluded.)
  if ((toState === 'COMPLETED' || toState === 'NEEDS_RETURN_VISIT') && meta.needsAISummaryBackfill) {
    try {
      const hasCustomerSummary = !!job.customerSummary;
      const hasTechnicalReport = !!job.aiSummary?.technicalReport;
      if (!hasCustomerSummary && !hasTechnicalReport) {
        const { generateJobSummary, saveSummaryToJob } = await import('./ai-summary.service');
        const summary = await generateJobSummary(job);
        await saveSummaryToJob(job.id!, summary, techId);
        console.log(`[JobSideEffects] AI summary backfilled for job ${job.id}`);
        // Re-read so downstream syncs see the summary
        try {
          const refresh = await getDoc(doc(db, 'jobs', job.id!));
          if (refresh.exists()) job = { id: refresh.id, ...refresh.data() } as Job;
        } catch {
          /* use previously-fetched job */
        }
      }
    } catch (err) {
      console.error('[JobSideEffects] AI summary backfill failed:', err);
      sideEffectErrors.push({ source: 'ai-summary-backfill', message: errMessage(err) });
    }
  }

  // ── #3a + #3b Pre-site completion → write presite data + advance deal ──
  if (toState === 'COMPLETED' && jobType === 'Pre-Site' && dealId) {
    try {
      const presiteData: Record<string, unknown> = {
        presiteCompletionNote: job.completionNote || '',
        presiteCompletedAt: now,
        presiteCompletedBy: techId,
        updatedAt: now,
      };
      if (job.fieldLog && job.fieldLog.length > 0) {
        presiteData.presiteFieldLogCount = job.fieldLog.length;
      }
      await updateDoc(doc(db, 'deals', dealId), presiteData);

      // Only advance if the deal is still at PRESITE. The check MUST live
      // inside advanceDealStage (expectedFromStage) rather than as a pre-read
      // here — a pre-read went stale whenever another completion path advanced
      // the deal in between, and advanceDealStage then advanced from QUOTING,
      // landing the deal in PROPOSAL_SENT with no proposal ever sent.
      const techName = await resolveTechName(techId);
      await advanceDealStage(
        dealId,
        techId,
        `System (Pre-Site Complete — ${techName})`,
        undefined,
        DealStage.PRESITE
      );
    } catch (err) {
      console.error('[JobSideEffects] Pre-site sync/advance failed:', err);
      sideEffectErrors.push({ source: 'presite-sync', message: errMessage(err) });
    }
  }

  // ── #4 Fault: sync completion back to fault ──
  if (jobType === 'Fault' && faultId) {
    try {
      await syncJobCompletionToFault(faultId, job, techId);
    } catch (err) {
      console.error('[JobSideEffects] Fault completion sync failed:', err);
      sideEffectErrors.push({ source: 'fault-sync', message: errMessage(err) });
    }
  }

  // ── #4b Service request: sync job completion back to the linked request ──
  // Jobs quick-raised from a service request carry `serviceRequestId` and the
  // request records the job in `linkedJobIds` — but completion previously never
  // flowed back, so the request timeline went silent once the tech finished.
  {
    const serviceRequestId = (job as { serviceRequestId?: string }).serviceRequestId;
    if (serviceRequestId) {
      try {
        const reqRef = doc(db, 'service_requests', serviceRequestId);
        const reqSnap = await getDoc(reqRef);
        if (reqSnap.exists()) {
          const reqData = reqSnap.data() as Record<string, unknown>;
          const techName = await resolveTechName(techId);
          const isReturn = toState === 'NEEDS_RETURN_VISIT';
          const detailParts = [
            isReturn
              ? `Tech flagged a return visit: ${job.returnVisitReason || 'no reason given'}`
              : `On-site work completed (${job.actualDuration || job.duration || 0}min).`,
            job.completionNote ? `Note: ${job.completionNote}` : '',
          ].filter(Boolean);
          await updateDoc(reqRef, {
            activityLog: arrayUnion({
              id: crypto.randomUUID(),
              timestamp: now,
              stage: reqData.stage || '01-New',
              author: techName,
              action: isReturn ? 'Linked job — return visit needed' : 'Linked job completed',
              details: detailParts.join(' '),
            }),
            updatedAt: now,
          });
        }
      } catch (err) {
        console.error('[JobSideEffects] Service-request completion sync failed:', err);
        sideEffectErrors.push({ source: 'service-request-sync', message: errMessage(err) });
      }
    }
  }

  // ── #5 + #10 Install: sync to deal + advance to REVIEW + activate services ──
  if (toState === 'COMPLETED' && jobType === 'Install' && dealId) {
    // Capture only the errors that originate from the install sync chain so we
    // don't conflate a fault-sync failure with the install status. The chain
    // pushes onto sideEffectErrors with sources prefixed `install-` (outer
    // wrap) or `install-service-activation*` (inner branches).
    const errorsBefore = sideEffectErrors.length;
    try {
      await syncInstallCompletionToDeal(job, dealId, techId, now, sideEffectErrors);
    } catch (err) {
      console.error('[JobSideEffects] Install completion sync failed:', err);
      sideEffectErrors.push({ source: 'install-sync', message: errMessage(err) });
    }
    const installErrors = sideEffectErrors
      .slice(errorsBefore)
      .filter((e) => e.source.startsWith('install-'));
    await recordInstallSyncStatus(action.docId, installErrors, now);
  }

  // ── #6 + #7 + #8 Return-visit flow ──
  if (action.type === 'return_visit' || toState === 'NEEDS_RETURN_VISIT') {
    try {
      await finalizeReturnVisit(job, dealId, techId, meta, now, sideEffectErrors);
    } catch (err) {
      console.error('[JobSideEffects] Return-visit finalize failed:', err);
      sideEffectErrors.push({ source: 'return-visit-finalize', message: errMessage(err) });
    }
  }

  // ── #11 P5.6 — Cable-run status propagation on commissioning-job COMPLETED ──
  // Markers we accept (in priority order):
  //   1. job.type === 'Commissioning' — the explicit legacy job-type label
  //   2. project.stages[projectStageId].stageType === 'commissioning' —
  //      the Projects v2 stage-tagged path. Read project doc once.
  // Either match fires the propagation. Fire-and-forget; failure is
  // captured in lastSideEffectError via the standard sideEffectErrors
  // bag, not via the projectAutoActionFailures collection (that's
  // P2.4 stage-action territory — cable propagation lives alongside
  // the other job-side-effects for consistency).
  if (toState === 'COMPLETED' && job.projectId && job.projectStageId) {
    try {
      let isCommissioning = job.type === 'Commissioning';
      if (!isCommissioning) {
        // Project doc read is cheap (one Firestore get) and we only
        // do it when the type-string fast-path didn't match. Caching
        // across multiple side effects would be nice but the other
        // effects don't need the project doc today — defer until a
        // second consumer materialises.
        try {
          const projSnap = await getDoc(doc(db, 'projects', job.projectId));
          if (projSnap.exists()) {
            const proj = projSnap.data() as Project;
            const stage = proj.stages?.find((s) => s.id === job.projectStageId);
            if (stage?.stageType === 'commissioning') isCommissioning = true;
          }
        } catch (err) {
          // Project read failure → can't determine stage type → skip
          // propagation rather than guess. PM-visible via the error
          // bag.
          console.warn('[JobSideEffects] Project read for stage-type check failed:', err);
          sideEffectErrors.push({
            source: 'cable-propagation-project-read',
            message: errMessage(err),
          });
        }
      }
      if (isCommissioning) {
        const techName = await resolveTechName(techId);
        const result = await propagateJobCompletionToCableRuns(
          job.projectId,
          job.projectStageId,
          { id: techId, name: `System (Commissioning job complete — ${techName})` }
        );
        if (result.failed > 0) {
          sideEffectErrors.push({
            source: 'cable-run-propagation',
            message: `${result.failed} runs failed to certify (${result.certified} certified, ${result.skipped} skipped)`,
          });
        }
        // Non-zero certified/skipped is informational — no error
        // pushed. The activity-log entry from bulkUpdateStatus
        // already captures what changed.
      }
    } catch (err) {
      console.error('[JobSideEffects] Cable-run propagation failed:', err);
      sideEffectErrors.push({ source: 'cable-run-propagation', message: errMessage(err) });
    }
  }

  // Persist any collected errors to the job doc so ops can see failed side effects.
  await recordSideEffectErrors(action.docId, sideEffectErrors);
  return { errors: sideEffectErrors };
}

// ============================================================================
// ERROR CAPTURE HELPERS
// ============================================================================

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * If any side effects failed during this transition, write a summary to the
 * job document so ops can see something went wrong. Uses a flat
 * `lastSideEffectError` field (not a subcollection) — smallest viable version
 * of the S1 workflowErrors pattern. Future S1 work can promote this to a
 * subcollection of all historic failures per entity.
 *
 * Schema on the job: { at, source, message }
 *   - `at`: ISO timestamp of the transition
 *   - `source`: comma-separated list of which effects failed (e.g. "fault-sync, install-sync")
 *   - `message`: pipe-separated list of the underlying error messages
 *
 * Writing this is itself fire-and-forget-safe: if the doc is gone or the
 * write fails, log and move on. Losing the error record is non-fatal.
 */
async function recordSideEffectErrors(
  jobId: string,
  errors: Array<{ source: string; message: string }>,
): Promise<void> {
  if (errors.length === 0) return;
  try {
    await updateDoc(doc(db, 'jobs', jobId), {
      lastSideEffectError: {
        at: new Date().toISOString(),
        source: errors.map((e) => e.source).join(', '),
        message: errors.map((e) => e.message).join(' | '),
      },
    });
  } catch (writeErr) {
    console.error(
      '[JobSideEffects] Failed to record lastSideEffectError on job (non-fatal):',
      writeErr,
    );
  }
}

/**
 * Persist the outcome of the install-completion → deal sync + service activation
 * chain to the job document. Distinct from `lastSideEffectError`:
 *   - lastSideEffectError captures the most recent failure across ALL effects
 *   - installSyncStatus is a discrete state — 'synced' if the install chain
 *     ran clean, 'failed' if any install-* source pushed an error.
 *
 * Lets ops query for jobs that need manual recovery
 * (where('installSyncStatus','==','failed')) without parsing string fields.
 *
 * Write is fire-and-forget-safe: a failure here is logged and swallowed
 * (we'd rather lose the marker than crash the side-effect pipeline).
 */
async function recordInstallSyncStatus(
  jobId: string,
  installErrors: Array<{ source: string; message: string }>,
  now: string,
): Promise<void> {
  const ok = installErrors.length === 0;
  try {
    const update: Record<string, unknown> = {
      installSyncStatus: ok ? 'synced' : 'failed',
      installSyncStatusAt: now,
    };
    if (ok) {
      // Clear any prior error message — the latest run succeeded.
      update.installSyncStatusError = deleteField();
    } else {
      update.installSyncStatusError = installErrors
        .map((e) => `${e.source}: ${e.message}`)
        .join(' | ');
    }
    await updateDoc(doc(db, 'jobs', jobId), update);
  } catch (writeErr) {
    console.error(
      '[JobSideEffects] Failed to record installSyncStatus on job (non-fatal):',
      writeErr,
    );
  }
}

// ============================================================================
// #5 + #10 — Install completion → deal + services
// ============================================================================

async function syncInstallCompletionToDeal(
  job: Job,
  dealId: string,
  techId: string,
  now: string,
  sideEffectErrors: Array<{ source: string; message: string }>,
): Promise<void> {
  const dealRef = doc(db, 'deals', dealId);
  const dealSnap = await getDoc(dealRef);
  if (!dealSnap.exists()) {
    // Deal was deleted between job creation and install completion. Pre-fix
    // this returned silently, leaving installSyncStatus unset (later
    // recorded as "synced" by the caller) — wrong. Push to sideEffectErrors
    // so the H5 status writer flips to 'failed' with a clear message.
    // Tracked as M5.
    const msg = `Deal ${dealId} not found — install sync skipped`;
    console.warn(`[JobSideEffects] ${msg}`);
    sideEffectErrors.push({ source: 'install-sync-deal-missing', message: msg });
    return;
  }
  const deal = dealSnap.data();
  const techName = await resolveTechName(techId);

  // Build install summary from fresh Firestore data
  const installSummary: Record<string, unknown> = {
    fieldLogCount: (job.fieldLog || []).length,
    photoCount: (job.photos || []).length,
    checklistCompleted: job.installChecklist?.completedAt ? true : false,
  };
  if (job.photos && job.photos.length > 0) {
    installSummary.photos = job.photos.map((p) => ({
      url: p.url,
      caption: p.caption || '',
      takenAt: p.timestamp,
      takenBy: p.techName || p.techId,
      // Conditionally include — Firestore rejects undefined inside nested
      // objects. Carries the tech's before/after/etc tag onto the deal.
      ...(p.category ? { category: p.category } : {}),
    }));
  }
  const jobAny = job;
  if (jobAny.customerSummary) installSummary.customerSummary = jobAny.customerSummary;
  if (jobAny.aiSummary?.technicalReport) installSummary.technicalReport = jobAny.aiSummary.technicalReport;
  if (job.completionNote) installSummary.completionNote = job.completionNote;
  if (job.additionalGearUsed && job.additionalGearUsed.length > 0) {
    installSummary.additionalGearUsed = job.additionalGearUsed;
  }
  // What the tech actually confirmed using on site — previously only the
  // planned materials were visible from the deal, so billing reconciliation
  // had to open the job to see field reality.
  if (job.usedMaterials && job.usedMaterials.length > 0) {
    installSummary.usedMaterials = job.usedMaterials.map((m) => {
      const item: Record<string, unknown> = {
        name: m.name,
        quantity: m.actualQuantity ?? m.quantity,
      };
      if (m.unit) item.unit = m.unit;
      if (m.sku) item.sku = m.sku;
      if (m.addedByTech) item.addedByTech = true;
      return item;
    });
  }
  // Tech's completion reasoning (feeds review + Otis context on the deal side)
  if (jobAny.completionReasoning && !jobAny.completionReasoning.skipped) {
    installSummary.completionReasoning = jobAny.completionReasoning;
  }

  const perJobSummary: Record<string, unknown> = {
    jobId: job.id,
    techName,
    duration: job.actualDuration || 0,
    completedAt: now,
    ...installSummary,
  };
  // DEEP strip — `photos`, `usedMaterials`, `additionalGearUsed` and
  // `completionReasoning` are nested arrays/objects copied off the job, so a
  // top-level pass leaves an undefined inside one of them and Firestore
  // rejects the whole deal update (losing installSummaries + the REVIEW
  // advance with it). Same trap as the return-visit builders below.
  const cleanedPerJobSummary = stripUndefinedDeep(perJobSummary);

  // Check if ALL install jobs for this deal are complete before advancing stage
  const installJobsQuery = query(
    collection(db, 'jobs'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('dealId', '==', dealId),
    where('type', '==', 'Install'),
  );
  const installJobsSnap = await getDocs(installJobsQuery);
  const allInstallJobs = installJobsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const pendingJobs = allInstallJobs.filter((j) => {
    const jj = j as Record<string, unknown>;
    return (
      jj.id !== job.id &&
      !jj.deleted &&
      jj.status !== 'Completed' &&
      jj.jobState !== 'COMPLETED' &&
      jj.jobState !== 'NEEDS_RETURN_VISIT'
    );
  });
  const allJobsDone = pendingJobs.length === 0;
  const totalJobs = allInstallJobs.filter((j) => !(j as Record<string, unknown>).deleted).length;

  const activityEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: techId,
    actorName: techName,
    action:
      totalJobs > 1
        ? `Install visit completed (${totalJobs - pendingJobs.length}/${totalJobs})`
        : 'Install job completed',
    // The tech's own wrap-up note, else the bare facts. This used to lead with
    // `customerSummary` — AI copy written for the post-job email, which then
    // read as the deal's account of the install. See ai-summary.service.ts.
    // Mirrored in functions/src/mcp/sync.ts — change both.
    details: jobAny.completionNote
      ? jobAny.completionNote
      : `Installation completed. ${job.actualDuration || 0}min on site.${
          !allJobsDone ? ` ${pendingJobs.length} visit(s) still pending.` : ''
        }`,
    automated: true,
  };

  const updates: Record<string, unknown> = {
    installCompletedAt: now,
    installCompletedBy: techId,
    installTechName: techName,
    installDuration: job.actualDuration || 0,
    installSummary: stripUndefinedDeep(installSummary),
    [`installSummaries.${job.id}`]: cleanedPerJobSummary,
    activityLog: arrayUnion(activityEntry),
    updatedAt: now,
  };

  const stageEligibleForReview =
    deal.stage === 'SCHEDULING' || deal.stage === 'INSTALLING' || deal.stage === 'REVIEW';

  if (allJobsDone && stageEligibleForReview && deal.stage !== 'REVIEW') {
    updates.stage = 'REVIEW';
    updates.stageHistory = arrayUnion({
      stage: 'REVIEW',
      enteredAt: now,
      completedBy: techId,
      notes:
        totalJobs > 1
          ? `Auto-advanced: all ${totalJobs} install visits completed — ready for office review`
          : 'Auto-advanced: install job completed — ready for office review',
    });
  } else if (!allJobsDone) {
    console.log(
      `[JobSideEffects] ${pendingJobs.length} install job(s) still pending for deal ${dealId} — not advancing to REVIEW yet`,
    );
  }

  await updateDoc(dealRef, updates);
  console.log(
    `[JobSideEffects] Synced install completion to deal ${dealId}${
      allJobsDone && stageEligibleForReview ? ' → REVIEW' : ' (partial — waiting for other jobs)'
    }`,
  );

  // #10 Create + activate customer services (only when all install jobs are done)
  // Feature-pack gate: service auto-activation is an MSP-pack behaviour. For a
  // tenant without the msp pack this silently no-ops — otherwise a completed
  // job mints customer_services rows the tenant has no surface to see.
  // MIRROR: functions/src/mcp/sync.ts applies the same gate server-side.
  if (allJobsDone && stageEligibleForReview && hasFeatureSync('service-activation')) {
    try {
      const fullDeal = { id: dealId, ...deal } as import('@/types/theo-workflow.types').Deal;
      const customerId = deal.customerId as string | undefined;
      const customerName = (deal.customerName as string | undefined) || job.client || 'Unknown';
      const tenantId = (deal.tenantId as string | undefined) || getTenantId();

      if (customerId) {
        const { createdIds: serviceIds, unresolvedPlans } = await activateServicePlansForCustomer(
          fullDeal,
          customerId,
          customerName,
          tenantId,
        );
        // A plan name that resolves to nothing still creates the service — it
        // just has no price and no planId, so it never bills, shows $0 in MRR,
        // and can't be repaired by a plan-wide price cascade. Route it to the
        // install-error channel (lastSideEffectError + installSyncStatus
        // 'failed') so ops can query for it instead of relying on a console
        // warning nobody reads.
        if (unresolvedPlans.length > 0) {
          sideEffectErrors.push({
            source: 'install-service-plan-unresolved',
            message:
              `Service created without a price — no active service plan matches ` +
              `${unresolvedPlans.map((p) => `"${p}"`).join(', ')}. ` +
              'Set the plan + price on /home/services before billing.',
          });
        }
        // Always run the sync pass — H7 option (a) means
        // activateServicePlansForCustomer creates services directly as
        // Active and activateServicesOnJobCompletion now stamps activation
        // timestamps + syncs Starlink kit metadata. This call must also fire
        // when serviceIds is empty: the pre-install service-confirmation
        // flow may have already created Pending Install services that the
        // dedup-by-dealId path skipped, and they need promoting too.
        await activateServicesOnJobCompletion(job);
        console.log(
          `[JobSideEffects] Created ${serviceIds.length} new service(s) + ran activation pass for deal ${dealId}`,
        );
      }
    } catch (err) {
      console.error('[JobSideEffects] Failed to create/activate customer services:', err);
      sideEffectErrors.push({ source: 'install-service-activation', message: errMessage(err) });
    }
  } else if (allJobsDone && !hasFeatureSync('service-activation')) {
    // Non-MSP tenant — recurring services aren't part of their packs, so the
    // skip is the correct behaviour, not an error. Console-only breadcrumb.
    console.log(
      `[JobSideEffects] Service activation skipped for deal ${dealId} — tenant packs don't include service-activation`,
    );
  } else if (allJobsDone) {
    // Deal stage moved off-track (reverted, fast-forwarded, LOST, etc.) between
    // install completion and this side effect running. Service creation is gated
    // on SCHEDULING/INSTALLING/REVIEW — without a record, the skip is invisible
    // and ops have no signal that services need to be created manually.
    const stageStr = typeof deal.stage === 'string' ? deal.stage : String(deal.stage);
    const skipMessage = `services not created — deal stage is "${stageStr}" (expected SCHEDULING, INSTALLING, or REVIEW)`;
    console.warn(`[JobSideEffects] ${skipMessage} (deal ${dealId})`);
    sideEffectErrors.push({
      source: 'install-service-activation-skipped',
      message: skipMessage,
    });
  }
}

// ============================================================================
// #6 + #7 + #8 — Return-visit finalisation
// ============================================================================

async function finalizeReturnVisit(
  originalJob: Job,
  dealId: string | null,
  techId: string,
  meta: Record<string, unknown>,
  now: string,
  sideEffectErrors: Array<{ source: string; message: string }>,
): Promise<void> {
  const reason =
    (meta.reason as string | undefined) ||
    originalJob.returnVisitReason ||
    '';
  const notes =
    (meta.notes as string | undefined) ||
    originalJob.returnVisitNotes ||
    '';
  const createFollowUpJob = (meta.createFollowUpJob as boolean | undefined) ?? true;
  const techName = await resolveTechName(techId);

  // Batch the follow-up job creation + the deal update so the deal's
  // returnVisitFollowUpJobId reverse-link can never get out of sync with
  // the actual job. Pre-fix these were two separate writes; if the deal
  // update failed after addDoc succeeded, an orphan follow-up job sat in
  // the unscheduled queue with no deal reference. Tracked as H6.
  const batch = writeBatch(db);
  let willCommit = false;
  let followUpJobId: string | null = null;

  // #7 Build the follow-up job (only for immediate-reschedule reasons)
  if (createFollowUpJob) {
    const fieldLogSummary = (originalJob.fieldLog || [])
      .slice(-10)
      .map((e: { techName?: string; text: string }) => `[${e.techName || 'Tech'}] ${e.text}`)
      .join('\n');

    const originalJobAny = originalJob;
    const followUpJob: Record<string, unknown> = {
      // Core identity
      client: originalJob.client || '',
      customerId: originalJob.customerId || null,
      address: originalJob.address || '',
      loc: originalJob.loc || '',
      // Carry the locality parts too. Copying `address` + `loc` alone left
      // every follow-up job with a street number and no town, which is what
      // the tech then saw on the day list. Mirrors functions/src/mcp/sync.ts.
      city: originalJob.city || '',
      state: originalJob.state || '',
      postcode: originalJob.postcode || '',
      // TODO(saas-readiness): default coords hardcoded — should read from
      // getJobFallbackCoords(tenant). This is a service (no useTenant); needs
      // the tenant config threaded through the call chain. Deep fallback —
      // originalJob almost always carries real coords.
      lat: originalJob.lat || -34.47,
      lng: originalJob.lng || 150.41,
      // Carry the ad-hoc site override with the address it describes — a
      // return visit to the same one-off site must still tell the tech it
      // isn't the customer's usual address (and stay revertable). Undefined
      // keys are stripped below, so normal jobs write nothing.
      addressOverride: originalJob.addressOverride,
      type: originalJob.type || 'Fault',
      jobType: originalJob.jobType || 'job',
      status: 'Scheduled',
      jobState: 'SCHEDULED',
      date: '',
      time: '',
      duration: originalJob.duration || 60,
      techId: null,
      techIds: [],

      // Links
      dealId: originalJob.dealId || null,
      dealCategory: originalJobAny.dealCategory || null,
      faultId: originalJobAny.faultId || null,
      projectId: originalJob.projectId || null,
      projectStageId: originalJob.projectStageId || null,

      // Context
      dealSnapshot: originalJobAny.dealSnapshot || null,
      services: originalJobAny.services || [],
      contact: originalJobAny.contact || '',
      contactName: originalJobAny.contactName || '',
      contactEmail: originalJobAny.contactEmail || '',

      // Access + credentials
      criticalNotes: originalJob.criticalNotes || '',
      vault: originalJob.vault || null,

      // Return visit context
      needsReturnVisit: false,
      returnVisitContext: {
        originalJobId: originalJob.id,
        originalCompletedAt: originalJob.completedAt || now,
        originalTechId: techId,
        originalTechName: techName,
        reason,
        techNotes: notes || '',
        previousFieldLog: fieldLogSummary || undefined,
        previousCompletionNote: originalJob.completionNote || undefined,
      },

      notes: originalJob.notes || '',
      description: `Return visit: ${reason}${notes ? ` — ${notes}` : ''}`,

      // Reset completion state
      checklist: [],
      checkpoints:
        originalJob.checkpoints?.map((cp) => {
          const reset: Record<string, unknown> = { ...cp, completed: false };
          delete reset.completedAt;
          delete reset.completedBy;
          return reset;
        }) || [],
      sos: false,
      deleted: false,
      lastAction: 'return_visit_created',
      createdAt: now,
      updatedAt: now,
      // Use canonical tenant resolution (localStorage with 'techery-au' fallback) —
      // replaces the legacy `|| 'default'` fallback that caused tenant drift.
      tenantId: originalJobAny.tenantId || getTenantId(),
    };

    // Strip undefined values (Firestore rejects them). DEEP, not top-level:
    // `returnVisitContext.previousFieldLog` / `.previousCompletionNote` are
    // `|| undefined`, and a job with no field log (the common case) sank the
    // whole return visit — `batch.set()` throws on a nested undefined, so no
    // follow-up job, no deal sync, no action item. Seen in production on
    // jobs/70ZJdnrf4zzFuXpuVN51 (2026-08-04, 'return-visit-finalize':
    // "Unsupported field value: undefined (found in field
    // returnVisitContext.previousFieldLog)").
    const cleanedFollowUp = stripUndefinedDeep(followUpJob);

    // Pre-allocate the doc ref so the deal update in the same batch can stamp
    // the reverse link before the job actually commits.
    const newJobRef = doc(collection(db, 'jobs'));
    batch.set(newJobRef, cleanedFollowUp);
    followUpJobId = newJobRef.id;
    willCommit = true;
  } else {
    console.log(
      `[JobSideEffects] Return visit blocked (${reason}) — no follow-up job created, syncing to source only`,
    );
  }

  // #8 Sync return-visit fields + activityLog to deal
  if (dealId) {
    const returnVisitStatus = createFollowUpJob ? 'ready_to_schedule' : 'blocked';
    const dealUpdate: Record<string, unknown> = {
      returnVisitRequired: true,
      returnVisitReason: reason,
      returnVisitStatus,
      returnVisitJobId: originalJob.id,
      returnVisitFlaggedAt: now,
      returnVisitFlaggedBy: techId,
      returnVisitTechName: techName,
      updatedAt: now,
    };
    if (notes) dealUpdate.returnVisitNotes = notes;
    if (followUpJobId) dealUpdate.returnVisitFollowUpJobId = followUpJobId;

    const activityEntry: Record<string, unknown> = {
      id: crypto.randomUUID(),
      timestamp: now,
      actor: techId,
      actorName: techName,
      action: createFollowUpJob
        ? `Return visit required — ${reason} (sent to unscheduled)`
        : `Return visit required — ${reason} (blocked, awaiting resolution)`,
      automated: true,
    };
    if (notes) activityEntry.details = notes;
    dealUpdate.activityLog = arrayUnion(activityEntry);

    batch.update(doc(db, 'deals', dealId), dealUpdate);
    willCommit = true;
  }

  // Commit atomically — either both writes land or neither does.
  if (willCommit) {
    try {
      await batch.commit();
      if (followUpJobId) {
        console.log(`[JobSideEffects] Created return-visit follow-up job: ${followUpJobId}`);
      }
      if (dealId) {
        console.log(`[JobSideEffects] Synced return visit to deal ${dealId}`);
      }
    } catch (err) {
      console.error('[JobSideEffects] Failed to commit return-visit batch:', err);
      sideEffectErrors.push({
        source: 'return-visit-batch',
        message: errMessage(err),
      });
      return; // Don't fire AI briefing if commit failed — the job doesn't exist.
    }
  }

  // Raise an action item so the return visit is tracked as office follow-up
  // work (it can be assigned, noted, and cleared) and surfaces alongside AI
  // action items. Fire-and-forget — a failure here must never fail the
  // completion. Idempotent-ish: only the ORIGINAL job carries the badge, and
  // the follow-up job is a fresh doc, so re-running won't double-stamp.
  try {
    const { createActionItem } = await import('./action-item.service');
    await createActionItem({
      title: `Return visit — ${reason || 'follow-up required'}`,
      detail: notes || undefined,
      source: 'return_visit',
      jobId: originalJob.id || null,
      jobType: originalJob.type,
      dealId: dealId || null,
      faultId: originalJob.faultId || null,
      customerId: originalJob.customerId || null,
      customerName: originalJob.client || 'Customer',
      address: originalJob.address || originalJob.loc || '',
      createdBy: { id: techId, name: techName },
      // Stamp only when there's no immediate follow-up job (a scheduled
      // follow-up already sits in the queue; a blocked one benefits from the
      // "!" badge on the original job) AND the job doesn't already carry an AI
      // action-item badge (don't clobber that link).
      stampJob: !createFollowUpJob && !originalJob.hasActionItem,
    });
  } catch (err) {
    console.warn('[JobSideEffects] Return-visit action item failed:', err);
  }
}

// ============================================================================
// promoteBlockedReturnVisitToReady
// ============================================================================

/**
 * Office-side action — promote a Blocked return visit to Ready once the
 * blocker is cleared (parts arrived, customer is available, access
 * arranged, etc). Creates the follow-up job that the tech's original
 * completion didn't create (because the blocked path skipped it) and
 * flips both the original job's + deal's returnVisitStatus from
 * 'blocked' to 'ready_to_schedule'.
 *
 * Mirrors the same follow-up-job shape that the immediate-reschedule
 * path produces in finalizeReturnVisit so downstream consumers
 * (scheduling, AI briefing, deal timeline) don't care which path the
 * job came from.
 *
 * @param originalJobId - The completed job that was flagged with a
 *                        blocked return-visit reason
 * @param promotedBy    - userId of the office staffer promoting it
 * @param promotedByName- Denormalized display name for activity log
 * @returns the new follow-up job's id
 */
export async function promoteBlockedReturnVisitToReady(
  originalJobId: string,
  promotedBy: string,
  promotedByName: string,
): Promise<string> {
  const originalSnap = await getDoc(doc(db, 'jobs', originalJobId));
  if (!originalSnap.exists()) {
    throw new Error('Original job not found');
  }
  const originalJob = { id: originalSnap.id, ...originalSnap.data() } as Job;
  const originalAny = originalJob as Job & {
    returnVisitStatus?: string;
    returnVisitFollowUpJobId?: string;
    returnVisitReason?: string;
    returnVisitNotes?: string;
    completedByName?: string;
    techNames?: string[];
    techName?: string;
  };

  // Defensive — caller should only invoke from a flagged-blocked surface,
  // but bad data + double-tap protection is worth the cheap check here.
  if (
    !originalAny.needsReturnVisit ||
    originalAny.returnVisitStatus !== 'blocked'
  ) {
    throw new Error(
      'Job is not a blocked return-visit candidate (needsReturnVisit + returnVisitStatus=blocked)',
    );
  }
  if (originalAny.returnVisitFollowUpJobId) {
    // Idempotency — if a follow-up was already created (e.g. by a race),
    // surface it instead of writing a duplicate.
    return originalAny.returnVisitFollowUpJobId as string;
  }

  const now = new Date().toISOString();
  const reason: string = originalAny.returnVisitReason || 'Return visit';
  const notes: string = originalAny.returnVisitNotes || '';
  const originalTechId: string = originalAny.completedBy || originalAny.techId || '';
  const originalTechName: string =
    originalAny.completedByName ||
    (originalAny.techNames?.[0] as string | undefined) ||
    originalAny.techName ||
    'Tech';

  // Build follow-up job — same shape as finalizeReturnVisit's createFollowUpJob
  // branch. Kept inline rather than extracted because the variance between
  // the two call sites is small and extraction risks coupling them.
  const fieldLogSummary = (originalJob.fieldLog || [])
    .slice(-10)
    .map((e: { techName?: string; text: string }) => `[${e.techName || 'Tech'}] ${e.text}`)
    .join('\n');

  const followUpJob: Record<string, unknown> = {
    client: originalJob.client || '',
    customerId: originalJob.customerId || null,
    address: originalJob.address || '',
    loc: originalJob.loc || '',
    // See the note in the sibling block above — locality parts travel with the
    // street line or the follow-up job shows a street number and no town.
    city: originalJob.city || '',
    state: originalJob.state || '',
    postcode: originalJob.postcode || '',
    lat: originalJob.lat || -34.47,
    lng: originalJob.lng || 150.41,
    // Carry the ad-hoc site override with the address it describes — a return
    // visit to the same one-off site must still tell the tech it isn't the
    // customer's usual address (and stay revertable). Undefined keys are
    // stripped below, so normal jobs write nothing.
    addressOverride: originalJob.addressOverride,
    type: originalJob.type || 'Fault',
    jobType: originalJob.jobType || 'job',
    status: 'Scheduled',
    jobState: 'SCHEDULED',
    date: '',
    time: '',
    duration: originalJob.duration || 60,
    techId: null,
    techIds: [],
    dealId: originalJob.dealId || null,
    dealCategory: originalAny.dealCategory || null,
    faultId: originalAny.faultId || null,
    projectId: originalJob.projectId || null,
    projectStageId: originalJob.projectStageId || null,
    dealSnapshot: originalAny.dealSnapshot || null,
    services: originalAny.services || [],
    contact: originalAny.contact || '',
    contactName: originalAny.contactName || '',
    contactEmail: originalAny.contactEmail || '',
    criticalNotes: originalJob.criticalNotes || '',
    vault: originalJob.vault || null,
    needsReturnVisit: false,
    returnVisitContext: {
      originalJobId: originalJob.id,
      originalCompletedAt: originalAny.completedAt || now,
      originalTechId,
      originalTechName,
      reason,
      techNotes: notes,
      previousFieldLog: fieldLogSummary || undefined,
      previousCompletionNote: originalJob.completionNote || undefined,
      // Distinct from the immediate path so we can audit which surface
      // produced this follow-up (Tech wrap-up vs. office promote).
      promotedFromBlocked: true,
      promotedAt: now,
      promotedBy,
      promotedByName,
    },
    notes: originalJob.notes || '',
    description: `Return visit (parts/blocker resolved): ${reason}${notes ? ` — ${notes}` : ''}`,
    checklist: [],
    checkpoints:
      originalJob.checkpoints?.map((cp) => {
        const reset: Record<string, unknown> = { ...cp, completed: false };
        delete reset.completedAt;
        delete reset.completedBy;
        return reset;
      }) || [],
    sos: false,
    deleted: false,
    lastAction: 'return_visit_created',
    createdAt: now,
    updatedAt: now,
    tenantId: originalAny.tenantId || getTenantId(),
  };

  // DEEP strip — same nested-undefined trap as the immediate path above
  // (`returnVisitContext.previousFieldLog` / `.previousCompletionNote`).
  const cleanedFollowUp = stripUndefinedDeep(followUpJob);

  const batch = writeBatch(db);
  const newJobRef = doc(collection(db, 'jobs'));
  batch.set(newJobRef, cleanedFollowUp);

  // Flip the original job from blocked → ready and stamp the reverse link.
  batch.update(doc(db, 'jobs', originalJobId), {
    returnVisitStatus: 'ready_to_schedule',
    returnVisitFollowUpJobId: newJobRef.id,
    returnVisitPromotedAt: now,
    returnVisitPromotedBy: promotedBy,
    returnVisitPromotedByName: promotedByName,
    updatedAt: now,
  });

  // Sync the deal too — same fields the immediate path writes, plus an
  // activityLog entry calling out that this was a manual promotion.
  if (originalJob.dealId) {
    const activityEntry = {
      id: crypto.randomUUID(),
      timestamp: now,
      actor: promotedBy,
      actorName: promotedByName,
      action: `Return visit promoted to Ready — ${reason} (follow-up job sent to unscheduled)`,
      automated: false,
    };
    batch.update(doc(db, 'deals', originalJob.dealId), {
      returnVisitStatus: 'ready_to_schedule',
      returnVisitFollowUpJobId: newJobRef.id,
      returnVisitPromotedAt: now,
      returnVisitPromotedBy: promotedBy,
      returnVisitPromotedByName: promotedByName,
      activityLog: arrayUnion(activityEntry),
      updatedAt: now,
    });
  }

  await batch.commit();

  return newJobRef.id;
}
